import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import {
  KeyRound,
  Flame,
  Lock,
  Palette,
  Users,
  ArrowRight,
  Check,
  Sparkles,
  MessageSquare,
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useRegion } from "../context/RegionContext";
import { usePlans, fmtAmountMinor, fmtInt } from "../lib/usePlans";
import { GeneratorCard } from "./GeneratorCard";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const FEATURE_KEYS = [
  { icon: Flame, k: "readOnce" },
  { icon: MessageSquare, k: "autoExpire" },
  { icon: Users, k: "noAccount" },
  { icon: KeyRound, k: "generator" },
  { icon: Lock, k: "secureRequest" },
  { icon: Palette, k: "orgReady" },
];

const STEP_KEYS = [
  { n: "01", k: "create" },
  { n: "02", k: "share" },
  { n: "03", k: "destroy" },
];

const LANDING_PLAN_ORDER = [
  { key: "free", highlight: false },
  { key: "pro", highlight: false },
  { key: "team", highlight: true, badge: true },
];

export const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("public");
  const { user } = useAuth();
  // Tarifs commerciaux = même source que /offres (publication active via /api/plans + RegionContext).
  const { region } = useRegion();
  const { plans, data } = usePlans(region);
  const currency = data?.currency || "EUR";
  const byKey = Object.fromEntries((plans || []).map((p) => [p.key, p]));

  const planCard = (cfg) => {
    const p = byKey[cfg.key];
    const isQuote = !p || p.requires_quote || p.base_price_minor == null;
    const priceStr = !p ? "—" : isQuote ? t("landing.plan.quote") : fmtAmountMinor(p.base_price_minor, currency);
    const feats = [];
    if (p) {
      const lim = p.monthly_secret_hard_limit ?? p.monthly_secret_soft_limit;
      feats.push(lim != null ? t("landing.plan.secretsPerMonth", { count: fmtInt(lim) }) : t("landing.plan.unlimitedSecrets"));
      if ((p.included_members || 0) > 1) feats.push(t("landing.plan.includedUsers", { count: p.included_members }));
      if (p.capabilities?.can_use_custom_domain) feats.push(t("landing.plan.customDomain"));
      else if (p.capabilities?.can_use_sso) feats.push(t("landing.plan.ssoIncluded"));
      else feats.push(t("landing.plan.zeroKnowledge"));
    }
    return {
      name: p?.name || cfg.key, priceStr, suffix: isQuote ? "" : t("pricing.perMonth"),
      features: feats.slice(0, 3), highlight: cfg.highlight, badge: cfg.badge,
    };
  };
  const landingPlans = LANDING_PLAN_ORDER.map(planCard);
  const biz = byKey["business"];
  const bizLabel = biz && !biz.requires_quote && biz.base_price_minor != null
    ? t("landing.pricing.businessLabel", { price: fmtAmountMinor(biz.base_price_minor, currency) })
    : t("landing.pricing.businessQuote");

  return (
    <div data-testid="landing-page" className="space-y-16 sm:space-y-20 pb-10">
      {/* HERO */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 lg:items-start pt-2">
        <div className="lg:col-span-6 lg:sticky lg:top-24">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold tracking-[0.14em] uppercase text-emerald-700">
              {t("landing.hero.badge")}
            </span>
          </motion.div>

          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
            {t("landing.hero.title")}{" "}
            <span className="text-emerald-600">{t("landing.hero.titleAccent")}</span>
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            <Trans
              t={t}
              i18nKey="landing.hero.desc"
              components={{ b: <span className="text-slate-900 font-semibold" /> }}
            />
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-cta-generate"
              onClick={() => navigate("/generateur")}
              className="h-12 px-7 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-emerald-950 font-bold text-base group"
            >
              <Lock className="w-4 h-4 mr-2" />
              {t("landing.hero.ctaCreate")}
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              data-testid="hero-cta-pricing"
              onClick={() => navigate("/offres")}
              variant="outline"
              className="h-12 px-7 border-slate-300 bg-white hover:bg-slate-50 text-slate-900 font-semibold text-base"
            >
              {t("landing.hero.ctaPricing")}
            </Button>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            {["readOnce", "autoExpire", "noInstall", "encryption"].map((c) => (
              <span key={c} className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-emerald-600" /> {t(`landing.hero.chips.${c}`)}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Real generator card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="absolute -inset-6 bg-gradient-to-tr from-emerald-500/10 via-cyan-500/10 to-transparent blur-2xl rounded-full pointer-events-none" />
          <div className="relative">
            <GeneratorCard variant="embedded" />
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section>
        <div className="mb-12">
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600">{t("landing.features.eyebrow")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 text-slate-900">
            {t("landing.features.title")}
          </h2>
          <p className="mt-3 text-slate-500 max-w-2xl">
            {t("landing.features.subtitle")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURE_KEYS.map((f, i) => (
            <motion.div
              key={f.k}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i % 3}
              className="glass rounded-2xl p-6 hover:-translate-y-1 transition-transform"
            >
              <div className="w-11 h-11 rounded-xl bg-emerald-500/12 border border-emerald-500/25 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1.5">{t(`landing.features.items.${f.k}.title`)}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{t(`landing.features.items.${f.k}.desc`)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="rounded-3xl bg-slate-900 p-8 sm:p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-40"
          style={{ background: "radial-gradient(ellipse at top right, rgba(16,185,129,0.25), transparent 60%)" }} />
        <div className="relative">
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-400">{t("landing.steps.eyebrow")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 mb-10 text-white">
            {t("landing.steps.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEP_KEYS.map((s, i) => (
              <motion.div
                key={s.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i}
              >
                <div className="font-heading text-5xl font-extrabold text-emerald-500/30 mb-3">{s.n}</div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">{t(`landing.steps.items.${s.k}.title`)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t(`landing.steps.items.${s.k}.desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section>
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600">{t("landing.pricing.eyebrow")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 text-slate-900">
            {t("landing.pricing.title")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {landingPlans.map((p, i) => (
            <motion.div
              key={p.name}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              className={`relative rounded-2xl p-6 flex flex-col ${
                p.highlight
                  ? "bg-emerald-500/[0.07] border-2 border-emerald-500/40 shadow-xl shadow-emerald-500/10"
                  : "glass"
              }`}
            >
              {p.badge && (
                <span className="inline-flex items-center gap-1 self-start mb-2 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                  <Sparkles className="w-3.5 h-3.5" /> {t("landing.pricing.popular")}
                </span>
              )}
              <div className="font-heading text-lg font-bold text-slate-900">{p.name}</div>
              <div className="font-heading text-3xl font-extrabold text-slate-900 mt-1 mb-4">
                {p.priceStr}
                <span className="text-sm font-medium text-slate-500">{p.suffix}</span>
              </div>
              <ul className="space-y-2 flex-1 mb-5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500 mb-4">
            {bizLabel}
          </p>
          <Button
            data-testid="pricing-preview-cta"
            onClick={() => navigate("/offres")}
            variant="outline"
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-900 font-semibold"
          >
            {t("landing.pricing.compareCta")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="rounded-3xl bg-gradient-to-r from-emerald-500 to-cyan-500 p-10 sm:p-14 text-center relative overflow-hidden">
        <div className="relative">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-emerald-950">
            {t("landing.finalCta.title")}
          </h2>
          <p className="mt-3 text-emerald-950/80 max-w-xl mx-auto">
            {t("landing.finalCta.desc")}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="final-cta-generate"
              onClick={() => navigate("/generateur")}
              className="h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white font-bold"
            >
              {t("landing.finalCta.ctaFree")}
            </Button>
            <Button
              data-testid="final-cta-account"
              onClick={() => navigate(user ? "/compte" : "/connexion?mode=register")}
              className="h-12 px-8 bg-white/90 hover:bg-white text-emerald-800 font-bold"
            >
              {user ? t("landing.finalCta.mySpace") : t("landing.finalCta.createAccount")}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
